import React, { useEffect, useState } from 'react';
import api from '../lib/api';
import {
  FileText, Shield, CheckCircle2, XCircle, Phone, Mail, User as UserIcon,
  Building2, Fingerprint, ScrollText, Sparkles, Clock, Target, Radio,
  ClipboardCheck, GitCommit, ShoppingCart, Truck, Award, KeyRound,
} from 'lucide-react';
import {
  ExclusivityBadge, MarketplaceStandardBadge, QualityScoreBadge, VerificationBadge,
} from './Badges';
import { TrustLevelBadge } from './TrustLevelBadge';

// Palette: Blue = verified, Grey = neutral, Green = success only, Gold = premium

const INTENT_LABELS = {
  information_request:  'Information Request',
  quote_requested:      'Quote Requested',
  callback_requested:   'Callback Requested',
  appointment_requested:'Appointment Requested',
  ready_to_buy:         'Ready to Buy',
};

const CHAIN_STEP_ICONS = {
  generated:         { Icon: Sparkles,       label: 'Generated' },
  submitted:         { Icon: FileText,       label: 'Submitted' },
  verified:          { Icon: ClipboardCheck, label: 'Verified' },
  duplicate_checked: { Icon: Fingerprint,    label: 'Duplicate Checked' },
  quality_scored:    { Icon: Target,         label: 'Quality Scored' },
  published:         { Icon: Radio,          label: 'Published' },
  purchased:         { Icon: ShoppingCart,   label: 'Purchased' },
  delivered:         { Icon: Truck,          label: 'Delivered' },
  accepted:          { Icon: Award,          label: 'Accepted' },
};

export default function LeadPassport({ leadId }) {
  const [data, setData] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!leadId) return;
    (async () => {
      try {
        const [pRes, lRes] = await Promise.all([
          api.get(`/leads/${leadId}/passport`),
          api.get(`/leads/${leadId}/ledger`).catch(() => ({ data: { entries: [] } })),
        ]);
        setData(pRes.data);
        setLedger(lRes.data?.entries || []);
      } catch (e) { /* silent */ }
      finally { setLoading(false); }
    })();
  }, [leadId]);

  if (loading) return <div className="text-xs font-mono text-gray-400 py-4">Loading passport…</div>;
  if (!data) return null;

  const p = data.passport || {};
  const verif = p.verification || {};
  const comp  = p.compliance  || {};
  const dup   = p.duplicate_check || {};
  const st    = data.seller_trust || {};
  const std   = st.marketplace_standard || {};

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="lead-passport">
      {/* Header */}
      <div className="px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <FileText className="w-4 h-4 text-[#0055FF]" strokeWidth={2.5} />
              <span className="text-xs uppercase tracking-[0.2em] text-[#0055FF] font-mono font-semibold">Lead Passport</span>
            </div>
            <div className="font-mono text-sm text-gray-800">{data.lead_id}</div>
            {data.digital_title && (
              <div className="mt-1.5 flex items-center gap-1.5" data-testid="digital-title">
                <KeyRound className="w-3 h-3 text-[#8B6914]" strokeWidth={2.5} />
                <span className="text-[10px] uppercase tracking-[0.2em] text-[#8B6914] font-mono font-semibold">Digital Title</span>
                <span className="font-mono text-xs text-gray-800 bg-[#FDF6E3] border border-[#D4AF37] rounded px-1.5 py-0.5">{data.digital_title}</span>
              </div>
            )}
            <div className="mt-1 text-xs text-gray-500">
              {data.source_label} · {data.category} · {data.region}
            </div>
          </div>
          <div className="text-right space-y-1.5">
            {typeof data.quality_score === 'number' && <QualityScoreBadge score={data.quality_score} />}
            {data.exclusivity && <ExclusivityBadge exclusivity={data.exclusivity} sharedCap={data.shared_cap} />}
          </div>
        </div>
      </div>

      {/* Provenance grid */}
      <div className="p-5 grid grid-cols-2 md:grid-cols-4 gap-4 border-b border-gray-100">
        <ProvenanceField label="Source"        value={data.source_label} />
        <ProvenanceField label="Campaign Type" value={data.campaign_type ? data.campaign_type.toUpperCase() : '—'} />
        <ProvenanceField label="Intent"        value={INTENT_LABELS[data.intent_level] || '—'} />
        <ProvenanceField label="Lead Age"      value={data.lead_age_hours != null ? `${data.lead_age_hours}h` : '—'} />
        <ProvenanceField label="Exclusivity"   value={data.exclusivity || '—'} valueClass="capitalize" />
        <ProvenanceField label="Sold Count"    value={data.sold_count ?? 0} />
        <ProvenanceField label="Verification Level" value={data.verification_level || '—'} valueClass="capitalize" />
        <ProvenanceField label="Duplicate Check"    value={dup.status ? dup.status.replace('_', ' ') : 'not run'} valueClass="capitalize" />
      </div>

      {/* Verification + Compliance chips */}
      <div className="p-5 border-b border-gray-100">
        <SectionTitle>Verification</SectionTitle>
        <div className="flex flex-wrap gap-1.5 mb-4" data-testid="passport-verification-chips">
          <ChipRow verified={verif.phone_verified}     label="Phone Verified"     Icon={Phone} />
          <ChipRow verified={verif.email_verified}     label="Email Verified"     Icon={Mail} />
          <ChipRow verified={verif.identity_confirmed} label="Identity Confirmed" Icon={UserIcon} />
          <ChipRow verified={verif.business_verified}  label="Business Verified"  Icon={Building2} />
        </div>
        <SectionTitle>Compliance</SectionTitle>
        <div className="flex flex-wrap gap-1.5" data-testid="passport-compliance-chips">
          <ChipRow verified={comp.consent_captured}       label="Consent Captured" />
          <ChipRow verified={comp.privacy_compliant}      label="Privacy Compliant" />
          <ChipRow verified={comp.do_not_contact_checked} label="DNC Checked" />
          {comp.verification_date && (
            <span className="inline-flex items-center gap-1 text-[11px] font-mono px-1.5 py-0.5 rounded border bg-gray-50 text-gray-600 border-gray-200">
              <Clock className="w-3 h-3" /> Verified {comp.verification_date}
            </span>
          )}
        </div>
      </div>

      {/* Source Evidence */}
      {p.source_evidence && Object.keys(p.source_evidence).length > 0 && (
        <div className="p-5 border-b border-gray-100">
          <SectionTitle>Source Evidence</SectionTitle>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs" data-testid="passport-source-evidence">
            {Object.entries(p.source_evidence).map(([k, v]) => (
              <div key={k} className="flex items-baseline gap-2 py-1 border-b border-gray-50">
                <span className="text-gray-500 uppercase tracking-wider text-[10px] font-mono min-w-[110px] whitespace-nowrap">{k.replace(/_/g, ' ')}</span>
                <span className="text-gray-800 font-mono break-all">{String(v)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Chain of Custody */}
      <div className="p-5 border-b border-gray-100">
        <SectionTitle>Chain of Custody</SectionTitle>
        <ol className="relative border-l border-gray-200 ml-2 space-y-3 mt-3" data-testid="passport-chain">
          {(p.chain_of_custody || []).map((step, i) => {
            const cfg = CHAIN_STEP_ICONS[step.step] || { Icon: GitCommit, label: step.step };
            const Icon = cfg.Icon;
            return (
              <li key={i} className="ml-4">
                <div className="absolute -left-2 flex items-center justify-center w-4 h-4 rounded-full bg-blue-50 border-2 border-white">
                  <Icon className="w-2.5 h-2.5 text-[#0055FF]" strokeWidth={2.5} />
                </div>
                <div className="flex items-baseline justify-between gap-3">
                  <div>
                    <div className="text-sm font-medium text-gray-900">{cfg.label}</div>
                    {step.by && <div className="text-xs text-gray-500 mt-0.5 font-mono">by {step.by}</div>}
                    {step.note && <div className="text-xs text-gray-500 mt-0.5">{step.note}</div>}
                  </div>
                  <div className="text-xs text-gray-400 font-mono whitespace-nowrap">
                    {new Date(step.at).toLocaleString()}
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      </div>

      {/* Quality Score breakdown */}
      {p.quality_score?.components && (
        <div className="p-5 border-b border-gray-100">
          <SectionTitle>Quality Score Breakdown</SectionTitle>
          <div className="mt-2 space-y-1.5" data-testid="passport-score-components">
            {p.quality_score.components.map(c => (
              <div key={c.key} className="flex items-center gap-3 text-xs">
                <div className="w-40 text-gray-500 uppercase tracking-wider font-mono text-[10px]">{c.key.replace(/_/g, ' ')}</div>
                <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full bg-[#0055FF]" style={{ width: `${(c.points / c.max) * 100}%` }} />
                </div>
                <div className="w-16 text-right font-mono text-gray-700">{c.points}/{c.max}</div>
              </div>
            ))}
          </div>
          <div className="mt-3 text-[10px] text-gray-400 font-mono">
            Deterministic scoring engine v{p.quality_score.version} · computed {new Date(p.quality_score.computed_at).toLocaleString()}
          </div>
        </div>
      )}

      {/* Immutable Ledger — Digital Title chain */}
      {ledger.length > 0 && (
        <div className="p-5 border-b border-gray-100">
          <SectionTitle>Immutable Ledger · Digital Title Chain</SectionTitle>
          <div className="text-[10px] text-gray-500 font-mono mt-1 mb-3">Append-only · every event recorded permanently</div>
          <div className="border border-gray-100 rounded overflow-hidden" data-testid="passport-ledger">
            <table className="w-full text-[11px]">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr className="text-left text-gray-500 uppercase tracking-wider">
                  <th className="px-3 py-1.5 font-mono font-semibold">Event</th>
                  <th className="px-3 py-1.5 font-mono font-semibold">Amount</th>
                  <th className="px-3 py-1.5 font-mono font-semibold">Actor</th>
                  <th className="px-3 py-1.5 font-mono font-semibold">Timestamp</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {ledger.map((e) => {
                  const isNegative = (e.amount_cents || 0) < 0;
                  const amount = e.amount_cents != null ? `${isNegative ? '-' : ''}$${Math.abs(e.amount_cents / 100).toFixed(2)}` : '—';
                  return (
                    <tr key={e.entry_id} className="hover:bg-gray-50/50">
                      <td className="px-3 py-1.5 font-mono text-gray-800">{e.event_type}</td>
                      <td className={`px-3 py-1.5 font-mono ${isNegative ? 'text-red-700' : 'text-gray-700'}`}>{amount}</td>
                      <td className="px-3 py-1.5 font-mono text-gray-500">{e.actor_alias || e.actor_id?.slice(0, 12) || '—'}</td>
                      <td className="px-3 py-1.5 font-mono text-gray-400">{new Date(e.recorded_at).toLocaleString()}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Seller Trust */}
      <div className="p-5">
        <SectionTitle>Seller Trust</SectionTitle>
        <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3">
          <MetricCell label="Trust Level" value={<TrustLevelBadge level={st.trust_level || 0} size="sm" />} />
          <MetricCell label="Trust Score" value={<span className="font-mono text-lg font-semibold text-[#0044CC]">{st.trust_score ?? '—'}<span className="text-xs text-gray-400">/100</span></span>} />
          <MetricCell label="Marketplace Standard" value={<MarketplaceStandardBadge tier={std.tier} label={std.label} size="sm" />} />
          <MetricCell label="Transactions" value={<span className="font-mono text-sm font-semibold text-gray-900">{st.reputation?.transactions_completed ?? 0}</span>} />
          <MetricCell label="Refund Rate" value={<span className="font-mono text-sm text-gray-900">{((st.reputation?.dispute_rate || 0) * 100).toFixed(1)}%</span>} />
          <MetricCell label="Repeat Buyers" value={<span className="font-mono text-sm text-gray-900">{st.reputation?.repeat_partners ?? 0}</span>} />
          <MetricCell label="Leads Sold" value={<span className="font-mono text-sm text-gray-900">{st.reputation?.leads_sold ?? 0}</span>} />
          <MetricCell label="Sell-through" value={<span className="font-mono text-sm text-gray-900">{((st.reputation?.sell_through_rate || 0) * 100).toFixed(0)}%</span>} />
        </div>
      </div>

      <div className="px-5 py-3 border-t border-gray-100 bg-gray-50 text-[11px] text-gray-500 flex items-center gap-2">
        <Shield className="w-3 h-3" strokeWidth={2.5} />
        Tamper-proof chain of custody. Every lead has a permanent, auditable passport.
      </div>
    </div>
  );
}

const SectionTitle = ({ children }) => (
  <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500 font-mono font-semibold">{children}</div>
);

const ProvenanceField = ({ label, value, valueClass = '' }) => (
  <div>
    <div className="text-[10px] uppercase tracking-wider text-gray-400 font-mono">{label}</div>
    <div className={`text-sm font-medium text-gray-900 mt-0.5 ${valueClass}`}>{value ?? '—'}</div>
  </div>
);

const ChipRow = ({ verified, label, Icon }) => (
  <span
    className={`inline-flex items-center gap-1 text-[11px] font-medium px-1.5 py-0.5 rounded border ${
      verified ? 'bg-blue-50 text-[#0044CC] border-blue-200' : 'bg-gray-50 text-gray-400 border-gray-200'
    }`}
  >
    {verified ? <CheckCircle2 className="w-3 h-3" strokeWidth={2.5} /> : <XCircle className="w-3 h-3" strokeWidth={2.5} />}
    {Icon && <Icon className="w-3 h-3" strokeWidth={2.5} />}
    {label}
  </span>
);

const MetricCell = ({ label, value }) => (
  <div className="border border-gray-100 rounded p-2 bg-gray-50/40">
    <div className="text-[10px] uppercase tracking-wider text-gray-400 font-mono">{label}</div>
    <div className="mt-1">{value}</div>
  </div>
);
